import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import { APIRoutes } from '@/services/api.routes';
import { toCurrencyCentValue } from '@/utils/helpers';
import type { OptionRight, JournalTradeDirection, JournalStrategy, JournalOptionTrade, JournalTag, JournalTagID } from '@/models';
import type { CreateJournalOptionTradeRequest, UpdateJournalOptionTradeRequest } from '@/modelsAPI';
import { STRATEGY_LABELS, JOURNAL_FIELD_CLASS, JOURNAL_LABEL_CLASS, readableTextColor } from '@/components/journal/journalHelpers';
import { showSuccessToast } from '@/utils/toasts';

interface OptionTradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (trade: JournalOptionTrade) => void;
  editTrade?: JournalOptionTrade | null;
}

interface FormErrors {
  symbolTicker?: string;
  strike?: string;
  expiry?: string;
  quantity?: string;
  openDate?: string;
  openPrice?: string;
  openFee?: string;
}

const STRATEGY_OPTIONS: JournalStrategy[] = ['KERNSTRATEGIE', 'REPARATURSTRATEGIE', 'EARNING_TRADE', 'UNKNOWN'];

const ERROR_TEXT_CLASS = 'mt-1 text-sm text-red-600 dark:text-red-400';
const INPUT_CLASS = `${JOURNAL_FIELD_CLASS} w-full disabled:opacity-60`;
const INVALID_INPUT_CLASS = `${INPUT_CLASS} border-red-500 dark:border-red-500`;

function centsToInput(cents: number): string {
  return (cents / 100).toString();
}

function inputClassFor(error: string | undefined): string {
  return error === undefined ? INPUT_CLASS : INVALID_INPUT_CLASS;
}

export default function OptionTradeModal({ isOpen, onClose, onSaved, editTrade = null }: OptionTradeModalProps) {
  const isEdit = editTrade !== null;

  const [symbolTicker, setSymbolTicker] = useState('');
  const [right, setRight] = useState<OptionRight>('PUT');
  const [direction, setDirection] = useState<JournalTradeDirection>('SELL_TO_OPEN');
  const [strategy, setStrategy] = useState<JournalStrategy>('KERNSTRATEGIE');
  const [strike, setStrike] = useState('');
  const [expiry, setExpiry] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [openDate, setOpenDate] = useState('');
  const [openPrice, setOpenPrice] = useState('');
  const [openFee, setOpenFee] = useState('0');
  const [note, setNote] = useState('');
  const [mood, setMood] = useState<number | null>(null);
  const [selectedTagIDs, setSelectedTagIDs] = useState<JournalTagID[]>([]);
  const [availableTags, setAvailableTags] = useState<JournalTag[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (!isOpen) return;
    setFormError(null);
    setFieldErrors({});
    APIRoutes.listJournalTags().then((resp) => setAvailableTags(resp.tags)).catch((err) => console.error('Failed to load tags:', err));
    if (editTrade !== null) {
      setSymbolTicker(editTrade.symbolTicker);
      setRight(editTrade.right);
      setDirection(editTrade.direction);
      setStrategy(editTrade.strategy);
      setStrike(centsToInput(editTrade.strike));
      setExpiry(editTrade.expiry);
      setQuantity(editTrade.quantity.toString());
      setOpenDate(editTrade.openDate);
      setOpenPrice(centsToInput(editTrade.openPrice));
      setOpenFee(centsToInput(editTrade.openFee));
      setNote(editTrade.note);
      setMood(editTrade.mood);
      setSelectedTagIDs(editTrade.tagIDs);
    } else {
      setSymbolTicker('');
      setRight('PUT');
      setDirection('SELL_TO_OPEN');
      setStrategy('KERNSTRATEGIE');
      setStrike('');
      setExpiry('');
      setQuantity('1');
      setOpenDate('');
      setOpenPrice('');
      setOpenFee('0');
      setNote('');
      setMood(null);
      setSelectedTagIDs([]);
    }
  }, [isOpen, editTrade]);

  const toggleTag = (tagID: JournalTagID) => {
    setSelectedTagIDs((prev) => prev.includes(tagID) ? prev.filter((id) => id !== tagID) : [...prev, tagID]);
  };

  const handleSubmit = async () => {
    const strikeNum = Number(strike);
    const openPriceNum = Number(openPrice);
    const qtyNum = Number(quantity);
    const feeNum = openFee.trim() === '' ? 0 : Number(openFee);

    const errors: FormErrors = {};
    if (symbolTicker.trim() === '') errors.symbolTicker = 'Symbol ist erforderlich.';
    if (strike.trim() === '' || Number.isNaN(strikeNum) || strikeNum <= 0) errors.strike = 'Bitte einen Strike größer als 0 eingeben.';
    if (openPrice.trim() === '' || Number.isNaN(openPriceNum) || openPriceNum < 0) errors.openPrice = 'Bitte eine Prämie von 0 oder mehr eingeben.';
    if (quantity.trim() === '' || !Number.isInteger(qtyNum) || qtyNum < 1) errors.quantity = 'Bitte eine ganze Anzahl ab 1 eingeben.';
    if (Number.isNaN(feeNum) || feeNum < 0) errors.openFee = 'Bitte eine Gebühr von 0 oder mehr eingeben.';
    if (expiry === '') errors.expiry = 'Verfall ist erforderlich.';
    if (openDate === '') errors.openDate = 'Eröffnungsdatum ist erforderlich.';
    // yyyy-MM-dd sorts lexicographically, so a string compare is a date compare here.
    if (expiry !== '' && openDate !== '' && expiry < openDate) errors.expiry = 'Der Verfall darf nicht vor dem Eröffnungsdatum liegen.';

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError(null);
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      let saved: JournalOptionTrade;
      if (editTrade !== null) {
        const request: UpdateJournalOptionTradeRequest = {
          strategy,
          note: note.trim(),
          strike: toCurrencyCentValue(strikeNum),
          expiry,
          quantity: qtyNum,
          openDate,
          openPrice: toCurrencyCentValue(openPriceNum),
          openFee: toCurrencyCentValue(feeNum),
          mood: mood ?? undefined,
          tagIDs: selectedTagIDs,
        };
        saved = await APIRoutes.updateJournalOptionTrade(editTrade.id, request);
      } else {
        const request: CreateJournalOptionTradeRequest = {
          symbolTicker: symbolTicker.trim().toUpperCase(),
          right,
          strike: toCurrencyCentValue(strikeNum),
          expiry,
          quantity: qtyNum,
          direction,
          strategy,
          openDate,
          openPrice: toCurrencyCentValue(openPriceNum),
          openFee: toCurrencyCentValue(feeNum),
          note: note.trim() === '' ? null : note.trim(),
          mood,
          tagIDs: selectedTagIDs,
        };
        saved = await APIRoutes.createJournalOptionTrade(request);
      }
      onSaved(saved);
      showSuccessToast(isEdit ? 'Trade aktualisiert' : 'Trade erstellt');
      onClose();
    } catch (err) {
      console.error('Failed to save option trade:', err);
      setFormError(isEdit ? 'Fehler beim Speichern' : 'Fehler beim Speichern. Ist das Symbol bekannt?');
    } finally {
      setSubmitting(false);
    }
  };

  const labelClass = JOURNAL_LABEL_CLASS;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Options-Trade bearbeiten' : 'Neuer Options-Trade'}
      icon={<FontAwesomeIcon icon={isEdit ? faEdit : faPlus} className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
      maxWidth="lg"
      closeDisabled={submitting}
    >
      <div className="p-6 space-y-4">
        {formError !== null && (
          <div role="alert" className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm">{formError}</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass} htmlFor="trade-symbol">Symbol</label>
            <input
              id="trade-symbol"
              className={inputClassFor(fieldErrors.symbolTicker)}
              value={symbolTicker}
              onChange={(e) => setSymbolTicker(e.target.value)}
              placeholder="AAPL"
              disabled={isEdit}
              aria-invalid={fieldErrors.symbolTicker !== undefined}
            />
            {fieldErrors.symbolTicker !== undefined && <p className={ERROR_TEXT_CLASS}>{fieldErrors.symbolTicker}</p>}
          </div>
          <div>
            <label className={labelClass} htmlFor="trade-right">Typ</label>
            <select id="trade-right" className={INPUT_CLASS} value={right} onChange={(e) => setRight(e.target.value as OptionRight)} disabled={isEdit}>
              <option value="PUT">PUT</option>
              <option value="CALL">CALL</option>
            </select>
          </div>
          <div>
            <label className={labelClass} htmlFor="trade-direction">Richtung</label>
            <select id="trade-direction" className={INPUT_CLASS} value={direction} onChange={(e) => setDirection(e.target.value as JournalTradeDirection)} disabled={isEdit}>
              <option value="SELL_TO_OPEN">Verkauf (Sell to open)</option>
              <option value="BUY_TO_OPEN">Kauf (Buy to open)</option>
            </select>
          </div>
          <div>
            <label className={labelClass} htmlFor="trade-strategy">Strategie</label>
            <select id="trade-strategy" className={INPUT_CLASS} value={strategy} onChange={(e) => setStrategy(e.target.value as JournalStrategy)}>
              {STRATEGY_OPTIONS.map((s) => (
                <option key={s} value={s}>{STRATEGY_LABELS[s]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass} htmlFor="trade-strike">Strike ($)</label>
            <input
              id="trade-strike"
              className={inputClassFor(fieldErrors.strike)}
              type="number"
              step="0.01"
              value={strike}
              onChange={(e) => setStrike(e.target.value)}
              placeholder="150.00"
              aria-invalid={fieldErrors.strike !== undefined}
            />
            {fieldErrors.strike !== undefined && <p className={ERROR_TEXT_CLASS}>{fieldErrors.strike}</p>}
          </div>
          <div>
            <label className={labelClass} htmlFor="trade-expiry">Verfall</label>
            <input
              id="trade-expiry"
              className={inputClassFor(fieldErrors.expiry)}
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              aria-invalid={fieldErrors.expiry !== undefined}
            />
            {fieldErrors.expiry !== undefined && <p className={ERROR_TEXT_CLASS}>{fieldErrors.expiry}</p>}
          </div>
          <div>
            <label className={labelClass} htmlFor="trade-quantity">Anzahl Kontrakte</label>
            <input
              id="trade-quantity"
              className={inputClassFor(fieldErrors.quantity)}
              type="number"
              step="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              aria-invalid={fieldErrors.quantity !== undefined}
            />
            {fieldErrors.quantity !== undefined && <p className={ERROR_TEXT_CLASS}>{fieldErrors.quantity}</p>}
          </div>
          <div>
            <label className={labelClass} htmlFor="trade-open-date">Eröffnet am</label>
            <input
              id="trade-open-date"
              className={inputClassFor(fieldErrors.openDate)}
              type="date"
              value={openDate}
              onChange={(e) => setOpenDate(e.target.value)}
              aria-invalid={fieldErrors.openDate !== undefined}
            />
            {fieldErrors.openDate !== undefined && <p className={ERROR_TEXT_CLASS}>{fieldErrors.openDate}</p>}
          </div>
          <div>
            <label className={labelClass} htmlFor="trade-open-price">Prämie / Kontrakt ($)</label>
            <input
              id="trade-open-price"
              className={inputClassFor(fieldErrors.openPrice)}
              type="number"
              step="0.01"
              value={openPrice}
              onChange={(e) => setOpenPrice(e.target.value)}
              placeholder="3.00"
              aria-invalid={fieldErrors.openPrice !== undefined}
            />
            {fieldErrors.openPrice !== undefined && <p className={ERROR_TEXT_CLASS}>{fieldErrors.openPrice}</p>}
          </div>
          <div>
            <label className={labelClass} htmlFor="trade-open-fee">Gebühr ($)</label>
            <input
              id="trade-open-fee"
              className={inputClassFor(fieldErrors.openFee)}
              type="number"
              step="0.01"
              value={openFee}
              onChange={(e) => setOpenFee(e.target.value)}
              aria-invalid={fieldErrors.openFee !== undefined}
            />
            {fieldErrors.openFee !== undefined && <p className={ERROR_TEXT_CLASS}>{fieldErrors.openFee}</p>}
          </div>
        </div>

        <div>
          <span className={labelClass} id="trade-mood-label">Stimmung</span>
          <div className="flex gap-2" role="group" aria-labelledby="trade-mood-label">
            {[1, 2, 3, 4, 5].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMood(mood === m ? null : m)}
                aria-label={`Stimmung ${m} von 5`}
                aria-pressed={mood === m}
                className={`w-9 h-9 rounded-lg text-sm font-medium cursor-pointer border ${mood === m ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        {availableTags.length > 0 && (
          <div>
            <span className={labelClass} id="trade-tags-label">Tags</span>
            <div className="flex flex-wrap gap-2" role="group" aria-labelledby="trade-tags-label">
              {availableTags.map((tag) => {
                const selected = selectedTagIDs.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    aria-label={`Tag ${tag.name}`}
                    aria-pressed={selected}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer border ${selected ? 'border-transparent' : 'text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'}`}
                    style={selected ? { backgroundColor: tag.color ?? '#2563eb', color: readableTextColor(tag.color ?? '#2563eb') } : undefined}
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <label className={labelClass} htmlFor="trade-note">Notiz</label>
          <textarea id="trade-note" className={INPUT_CLASS} rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Gedanken zum Trade..." />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" disabled={submitting} onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" loading={submitting} onClick={handleSubmit}>Speichern</Button>
        </div>
      </div>
    </Modal>
  );
}
